import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { Toast } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { setErrorListener } from '@/store/errorMiddleware';
import { ToastContext } from '@/hooks/useToast';
import { StyledToastContainer } from './ToastProvider.styled';

interface ToastItem {
  id: number;
  message: string;
  variant: 'danger' | 'success' | 'warning';
}

let nextId = 0;

export function ToastProvider({ children }: { children: ReactNode }) {
  const { t } = useTranslation();
  const [toasts, setToasts] = useState<ToastItem[]>([]);

  const addToast = useCallback((message: string, variant: ToastItem['variant']) => {
    const id = nextId++;
    setToasts((prev) => [...prev, { id, message, variant }]);
  }, []);

  const removeToast = useCallback((id: number) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  }, []);

  const showError = useCallback(
    (message: string) => addToast(message, 'danger'),
    [addToast],
  );

  const showSuccess = useCallback(
    (message: string) => addToast(message, 'success'),
    [addToast],
  );

  // Register as the global error listener for RTK Query middleware
  useEffect(() => {
    setErrorListener(showError);
    return () => setErrorListener(null);
  }, [showError]);

  return (
    <ToastContext.Provider value={{ showError, showSuccess }}>
      {children}
      <StyledToastContainer position="top-end" className="p-3">
        {toasts.map((toast) => (
          <Toast
            key={toast.id}
            bg={toast.variant}
            onClose={() => removeToast(toast.id)}
            delay={5000}
            autohide
          >
            <Toast.Header>
              <strong className="me-auto">
                {toast.variant === 'danger' ? t('common.error') : 'Scribia'}
              </strong>
            </Toast.Header>
            <Toast.Body className={toast.variant === 'danger' ? 'text-white' : ''}>
              {toast.message}
            </Toast.Body>
          </Toast>
        ))}
      </StyledToastContainer>
    </ToastContext.Provider>
  );
}
